import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, Receipt, Percent, Wallet } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Factuur, fetchFactuur } from '../../../services/orderhistory/factuurService';
import { factuurOrdersToOrderHistory } from '../../../utils/factuurUtils';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useAppSettingsStore } from '../../../stores/appSettingsStore';
import { calculateFactuurCategorySpentBreakdown } from '../../../utils/chartsUtils';
import { calculateProductTotal } from '../../../utils/platformUtils';
import { currencyFormatter, formatDateLong, formatPlatformName } from '../../../services/helperService';
import { getCategoryStyles, getMainCategoryDisplayName } from '../../orderhistory/categoryUtils';
import { formatCategoryName } from '../../orderhistory/sections/FilterControls';
import { getOrderTotal, getOrderVatAmount, getOrderVatRate, formatTaxRate } from '../../orderhistory/vatUtils';
import { DocumentInformation, DocumentInfoField, DocumentFileLink } from '../../orderhistory/DocumentInformation';
import { ProductDataItem } from '../../orderhistory/ProductDataItem';
import { SpendingSplitBar } from '../../orderhistory/SpendingSplitBar';
import { OrderVatToggle } from '../../orderhistory/sections/OrderVatToggle';
import { StatCard } from '../../ui/StatCard';
import { SupplierLogo } from '../../ui/SupplierLogo';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import AppFooter from '../../common/AppFooter';
import { iconBgBlue, iconBgAmber, iconBgGreen, smallTextSize, mediumText, roundedLG } from '../../../styling/stylingHelper';

export default function FactuurDetailPage() {
  const { factuurId } = useParams<{ factuurId: string }>();
  const { t } = useTranslation();
  const darkMode = useAppSettingsStore(s => s.darkMode);
  const [factuur, setFactuur] = useState<Factuur | null>(null);
  const [failed, setFailed] = useState(false);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurants = useRestaurantStore(s => s.restaurants);
  const showExVat = useAppUserDefaultStore(s => s.showExVatOrders);

  useEffect(() => {
    const controller = new AbortController();
    setFactuur(null);
    setFailed(false);
    if (factuurId) {
      fetchFactuur(factuurId, controller.signal).then(value => {
        if (!controller.signal.aborted) setFactuur(value);
      }).catch(error => {
        if (controller.signal.aborted) return;
        console.error('Failed to load invoice', error);
        setFailed(true);
      });
    }
    document.getElementById('mainContainer')?.scrollTo(0, 0);
    return () => controller.abort();
  }, [factuurId]);

  const orders = useMemo(() => factuur ? factuurOrdersToOrderHistory(factuur, platformToSectionIndex) : [],
    [factuur, platformToSectionIndex]);
  const breakdown = useMemo(() => calculateFactuurCategorySpentBreakdown(orders, showExVat), [orders, showExVat]);
  const sides = (['food', 'drinks'] as const).map(side => ({
    side,
    rows: breakdown.filter(row => row.side === side),
    amount: breakdown.filter(row => row.side === side).reduce((sum, row) => sum + row.amount, 0),
  })).filter(group => group.rows.length > 0);
  const sideTotal = sides.reduce((sum, group) => sum + group.amount, 0);
  const products = orders.flatMap(order => order.productData);
  const gross = factuur?.totalWithTaxes ?? factuur?.total ?? 0;
  const vat = factuur && factuur.totalWithTaxes != null ? getOrderVatAmount(factuur.platform, gross, factuur.total, factuur.shipping ?? 0) : 0;
  // Same guard as the list: without a printed incl. VAT total the rate is unknown, not 0%.
  const taxRate = factuur && factuur.totalWithTaxes != null ? getOrderVatRate(factuur.platform, gross, factuur.total, factuur.shipping ?? 0, products) : undefined;
  const [openOrderIds, setOpenOrderIds] = useState<Set<string>>(new Set());
  const toggleOrder = (id: string) => setOpenOrderIds(current => {
    const next = new Set(current);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  const restaurantName = restaurants.find(restaurant => restaurant.id === factuur?.restaurantId)?.name;

  return (
    <div>
      <MobilePageHeader title={t('orderHistory.facturen.details')} />
      <Link to="/orderhistory/orders?view=facturen" className="inline-flex items-center gap-2 mb-4 text-text dark:text-text-dark">
        <ArrowLeft className="w-5 h-5" />{t('orderHistory.facturen.back')}
      </Link>
      {!factuur ? (
        <p role={failed ? 'alert' : 'status'} className="py-8 text-center text-muted dark:text-muted-dark">
          {t(failed ? 'orderHistory.facturen.detailLoadError' : 'orderHistory.facturen.loading')}
        </p>
      ) : (
        <div className="flex flex-col gap-6 mb-6">
          <div className="flex flex-wrap justify-center items-center gap-3 bg-card dark:bg-card-dark rounded-xl p-6">
            <SupplierLogo platform={factuur.platform} size="large" alt={factuur.platform} />
            <h1 className="text-2xl font-semibold break-all text-text dark:text-text-dark">{factuur.invoiceNumber}</h1>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={<Receipt className="w-6 h-6" style={{ color: '#2E90FA' }} />} iconBg={iconBgBlue}
              title={t('orderHistory.total')} value={factuur.total} valueType="currency" />
            <StatCard icon={<Percent className="w-6 h-6" style={{ color: '#F59E0B' }} />} iconBg={iconBgAmber}
              title={t('orderHistory.vat')} value={vat} valueType="currency" />
            <StatCard icon={<Wallet className="w-6 h-6" style={{ color: '#079455' }} />} iconBg={iconBgGreen}
              title={t('orderHistory.totalWithTaxes')} value={gross} valueType="currency" />
          </div>
          <DocumentInformation title={t('orderHistory.facturen.information')}>
            <DocumentInfoField label={t('common.restaurant')}>{restaurantName ?? '—'}</DocumentInfoField>
            <DocumentInfoField label={t('orderHistory.platform')}>{formatPlatformName(factuur.platform)}</DocumentInfoField>
            <DocumentInfoField label={t('orderHistory.facturen.invoiceNumber')}>{factuur.invoiceNumber}</DocumentInfoField>
            <DocumentInfoField label={t('orderHistory.facturen.invoiceDate')}>{formatDateLong(new Date(factuur.invoiceDate))}</DocumentInfoField>
            <DocumentInfoField label={t('orderHistory.vatRate')}>{formatTaxRate(taxRate) || '—'}</DocumentInfoField>
            {factuur.discount != null && <DocumentInfoField label={t('orderHistory.discount')}>{currencyFormatter.format(factuur.discount)}</DocumentInfoField>}
            {factuur.shipping != null && <DocumentInfoField label={t('orderHistory.shipping')}>{currencyFormatter.format(factuur.shipping)}</DocumentInfoField>}
            {factuur.sourceFileUrl && <DocumentInfoField label={t('orderHistory.factuurFile')}><DocumentFileLink href={factuur.sourceFileUrl} /></DocumentInfoField>}
          </DocumentInformation>
          <div className="flex flex-wrap justify-between items-center gap-3">
            <h2 className="text-lg font-semibold text-text dark:text-text-dark">{t('orderHistory.facturen.categoryBreakdown')}</h2>
            <OrderVatToggle />
          </div>
          {/* Percentage bars only represent non-negative parts. Signed category amounts remain below. */}
          {sideTotal > 0 && sides.every(group => group.amount >= 0) && (
            <SpendingSplitBar title={t('orderHistory.facturen.categoryBreakdown')} segments={sides.map(group => ({
              label: getMainCategoryDisplayName(group.side.toUpperCase()) || group.side,
              pct: group.amount / sideTotal * 100,
              color: group.side === 'food' ? '#2E90FA' : '#F59E0B',
            }))} />
          )}
          {sides.length === 0 ? <p className="text-muted dark:text-muted-dark">{t('orderHistory.noProductsFound')}</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sides.map(group => (
                <section key={group.side} className="bg-card dark:bg-card-dark rounded-xl p-4">
                  <h3 className="font-semibold mb-3 text-text dark:text-text-dark">{getMainCategoryDisplayName(group.side.toUpperCase())}</h3>
                  <dl className="space-y-3">
                    {group.rows.map(row => (
                      <div key={`${row.mainCategory}.${row.categoryKey ?? ''}`} className="flex flex-wrap items-center justify-between gap-3 text-text dark:text-text-dark">
                        <dt>{row.categoryKey ? (
                          <span className={cn(smallTextSize, mediumText, roundedLG, 'inline-block px-2 py-1')}
                            style={getCategoryStyles(row.categoryKey, darkMode)}>
                            {formatCategoryName(row.categoryKey, row.mainCategory)}
                          </span>
                        ) : t('products.export.uncategorized')}</dt>
                        <dd className="font-semibold whitespace-nowrap">{currencyFormatter.format(row.amount)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          )}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-text dark:text-text-dark">{t('orderHistory.orders')}</h2>
            {orders.map((order, index) => {
              const toggleable = orders.length > 1;
              const isOpen = !toggleable || openOrderIds.has(order.id);
              const { orderSourceFileUrl, orderHistoryId } = factuur.orders[index];
              return (
                <div key={order.id} className="bg-card dark:bg-card-dark rounded-xl overflow-hidden">
                  {/* The whole row toggles; the links stop propagation so they only navigate. */}
                  <div onClick={toggleable ? () => toggleOrder(order.id) : undefined}
                    className={cn('flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-2 px-4 py-3 text-text dark:text-text-dark',
                      toggleable && 'cursor-pointer hover:bg-muted/10 dark:hover:bg-muted-dark/10 transition-colors')}>
                    <button type="button" aria-expanded={isOpen} disabled={!toggleable}
                      className="flex flex-1 min-w-0 items-center gap-3 text-left cursor-pointer disabled:cursor-default">
                      {toggleable && <ChevronDown className={cn('w-4 h-4 shrink-0 text-muted dark:text-muted-dark transition-transform', isOpen && 'rotate-180')} />}
                      <span className="font-semibold truncate">{order.orderNumber}</span>
                      <span className="text-sm text-muted dark:text-muted-dark whitespace-nowrap">{formatDateLong(new Date(order.orderDate))}</span>
                    </button>
                    <span className="font-semibold whitespace-nowrap">{currencyFormatter.format(getOrderTotal(order, showExVat))}</span>
                    {(orderSourceFileUrl || orderHistoryId) && (
                      <div className="flex items-center gap-4 w-full sm:w-auto" onClick={event => event.stopPropagation()}>
                        {orderSourceFileUrl && <DocumentFileLink href={orderSourceFileUrl} />}
                        {orderHistoryId && <Link to={`/orderhistory/${orderHistoryId}`}
                          className="text-sm whitespace-nowrap text-blue-600 dark:text-blue-400 hover:underline">{t('orderHistory.facturen.openOrder')}</Link>}
                      </div>
                    )}
                  </div>
                  {isOpen && (
                    <div className="space-y-2 px-4 py-3 border-t border-border dark:border-border-dark">
                      {[...order.productData].sort((a, b) => calculateProductTotal(b, order.platform, showExVat) - calculateProductTotal(a, order.platform, showExVat))
                        .map((product, productIndex) => <ProductDataItem key={productIndex} product={product} platform={order.platform} showExVat={showExVat} responsive />)}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      )}
      <AppFooter />
    </div>
  );
}
